import { useContext, useMemo } from "react";
import tinycolor from "tinycolor2";
import { Box, Typography, FormGroup } from "@mui/material";
import { useSelector } from "react-redux";
import { AppContext } from "../../App";
import { selectChartConfig } from "../../selectors/chart";
import { HistogramSeriesOptions } from "../chartV2/chart.typings";
import ColorSelector from "../colorSelector/colorSelector";
import CommonChartSettingsTabs from "./commonChartSettingsTabs";
import {
    chartSettingsFormGroupSx,
    chartSettingsColorRowLabelSx,
    chartSettingsGridRowSx,
    chartSettingsRowControlsSx,
    chartSettingsSectionTitleSx,
} from "./chartSettingsLayout";

type Props = {
    viewKey: string;
};

const ColumnSettingsDialogs = ({ viewKey }: Props) => {
    const appContext = useContext(AppContext)
    const chart = appContext.getChart(viewKey)
    const chartConfig = useSelector(selectChartConfig(viewKey))
    const primarySeriesGroupId = useMemo(() => {
        return chart?.primarySeriesGroup?.config?.id
    }, [chart?.primarySeriesGroup?.config?.id])

    const upColor = useMemo(() => {
        let color = '#26a69a80'
        if (primarySeriesGroupId) {
            const seriesId = chartConfig?.seriesGroups?.[primarySeriesGroupId]?.series?.[0]
            const series = chartConfig.series?.[seriesId ?? ''].options
            color = (series as any)?.upColor ?? '#26a69a80'
        }
        return tinycolor(color)
    }, [chartConfig.series, chartConfig?.seriesGroups, primarySeriesGroupId])

    const downColor = useMemo(() => {
        let color = '#ef535080'
        if (primarySeriesGroupId) {
            const seriesId = chartConfig?.seriesGroups?.[primarySeriesGroupId]?.series?.[0]
            const series = chartConfig.series?.[seriesId ?? ''].options
            color = (series as any)?.downColor ?? '#ef535080'
        }
        return tinycolor(color)
    }, [chartConfig.series, chartConfig?.seriesGroups, primarySeriesGroupId])

    const handleColorChangeSeries = (newColor: string, type: keyof HistogramSeriesOptions) => {
        if (chart) {
            chart.primarySeriesGroup?.getPrimarySeries()?.updateOptions({
                [type]: newColor
            } as Partial<HistogramSeriesOptions>)
        }
    }

    const symbolTabContent = (
        <Box>
            <Typography variant="subtitle1" sx={chartSettingsSectionTitleSx}>
                COLUMNS
            </Typography>
            <FormGroup sx={chartSettingsFormGroupSx}>
                <Box sx={chartSettingsGridRowSx}>
                    <Typography sx={chartSettingsColorRowLabelSx}>Up Color</Typography>
                    <Box sx={chartSettingsRowControlsSx}>
                        <ColorSelector
                            compact
                            showOnlySwatch
                            color={upColor.toHexString()}
                            opacity={upColor.getAlpha()}
                            onColorChange={(color, transparency) => {
                                const newColor = tinycolor(color).setAlpha(transparency).toHex8String()
                                handleColorChangeSeries(newColor, 'upColor')
                            }}
                        />
                    </Box>
                </Box>
                <Box sx={chartSettingsGridRowSx}>
                    <Typography sx={chartSettingsColorRowLabelSx}>Down Color</Typography>
                    <Box sx={chartSettingsRowControlsSx}>
                        <ColorSelector
                            compact
                            showOnlySwatch
                            color={downColor.toHexString()}
                            opacity={downColor.getAlpha()}
                            onColorChange={(color, transparency) => {
                                const newColor = tinycolor(color).setAlpha(transparency).toHex8String()
                                handleColorChangeSeries(newColor, 'downColor')
                            }}
                        />
                    </Box>
                </Box>
            </FormGroup>
        </Box>
    );

    return (
        <CommonChartSettingsTabs
            viewKey={viewKey}
            symbolTabContent={symbolTabContent}
        />
    );
};

export default ColumnSettingsDialogs;
